// MOMM API Integration Module
// Handles all API endpoints for MOMM functionality
// This module provides a unified interface that matches the SQLite database operations
//
// Performance Optimization:
// - getAllChats() only fetches chat metadata (no messages) for faster initial load
// - Messages are fetched on-demand when a chat is clicked via getChatById()
// - All API calls include cache-busting timestamps to ensure fresh data

import Swal from 'sweetalert2';
import { getMommApiConfig, getApiBaseUrl } from '../api-helper';
import electronBridge from '../utils/electronBridge';

// Helper function to get API configuration
const getApiConfig = async () => {
  const config = await getMommApiConfig();
  
  // Debug logging
  console.log('MOMM API Config:', {
    hasApiKey: !!config.apiKey,
    hasSessionKey: !!config.sessionKey,
    accountNo: config.accountNo,
    hasVerificationToken: !!config.verificationToken,
    hasEmailHash: !!config.emailHash,
    isConfigured: config.isConfigured
  });
  
  if (!config.isConfigured) {
    throw new Error('MOMM API key not configured. Please add it in settings.');
  }
  
  const headers = {
    'Authorization': `Bearer ${config.sessionKey}`,
    'Content-Type': 'application/json'
  };
  
  return {
    apiKey: config.apiKey,
    sessionKey: config.sessionKey,
    accountNo: config.accountNo,
    verificationToken: config.verificationToken,
    emailHash: config.emailHash,
    headers
  };
};

const openPromptusCreditsStore = async (webPage) => {
  const config = await getApiConfig();
  console.log(`Opening Promptus Credits Store Page: `, webPage);
  if (electronBridge.isElectron) {
      electronBridge.ipcRenderer.send('open-urlInExternal', `https://login.promptus.ai/auto-signin?v=${config.verificationToken}&e=${config.emailHash}&a=${config.accountNo}&r=/${webPage}`)
  } else {
      window.open(`https://login.promptus.ai/auto-signin?v=${config.verificationToken}&e=${config.emailHash}&a=${config.accountNo}&r=/${webPage}`, '_blank');
  }
};


// Store active abort controllers for request cancellation
const activeRequests = new Map();

// Helper function for API calls with timeout and cancellation support
const apiCall = async (endpoint, options = {}) => {
  // Generate a unique request ID if not provided
  const requestId = options.requestId || `${endpoint}-${Date.now()}`;
  
  // Cancel any existing request with the same ID
  if (activeRequests.has(requestId)) {
    const existingController = activeRequests.get(requestId);
    existingController.abort();
    activeRequests.delete(requestId);
    console.log(`🚫 Cancelled existing request: ${requestId}`);
  }
  
  // Create new AbortController for this request
  const abortController = new AbortController();
  activeRequests.set(requestId, abortController);
  
  try {
    const config = await getApiConfig();
    const mommConfig = await getMommApiConfig();
    const url = `${mommConfig.apiUrl}${endpoint}`;
    
    // Enhanced debug logging
    console.log('MOMM API Request:', {
      url,
      method: options.method || 'GET',
      requestId,
      headers: {
        ...config.headers,
        ...options.headers
      },
      accountNo: config.accountNo,
      hasSessionKey: !!config.sessionKey
    });
    
    // Set up timeout (default 30 seconds, configurable)
    const timeout = options.timeout || 120000;
    const timeoutId = setTimeout(() => {
      abortController.abort();
      console.error(`⏱️ Request timeout after ${timeout}ms: ${requestId}`);
    }, timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: abortController.signal,
        headers: {
          ...config.headers,
          ...options.headers
        }
      });
      
      // Clear timeout on successful response
      clearTimeout(timeoutId);
    
    if (!response.ok) {
      let errorMessage = `API request failed: ${response.status}`;
      let errorDetails = {};
      
      try {
        const error = await response.json();
        errorMessage = error.error || error.message || errorMessage;
        errorDetails = error;
        
        // Debug log error response
        console.log('MOMM API Error Response:', {
          url,
          status: response.status,
          errorDetails,
          headers: Object.fromEntries(response.headers.entries())
        });
      } catch (jsonError) {
        // If response is not JSON, use status text
        errorMessage = `${response.status} ${response.statusText}`;
        console.log('MOMM API Non-JSON Error:', {
          url,
          status: response.status,
          statusText: response.statusText
        });
      }
      
      // Handle specific error codes
      if (response.status === 401) {
        // Check for specific authentication issues
        if (errorMessage.toLowerCase().includes('momm_api_key not set') || errorMessage.toLowerCase().includes('momm_api_key')) {
          throw new Error('Your session has expired. Please log out and log back in to continue using MOMM.');
        } else if (errorMessage.toLowerCase().includes('invalid token') || errorMessage.toLowerCase().includes('invalid api key')) {
          throw new Error('Authentication failed. Your session may have expired. Please log in again.');
        } else if (errorMessage.toLowerCase().includes('missing')) {
          throw new Error('Authentication failed. Missing required credentials. Please ensure you are logged in.');
        }
        throw new Error('Authentication failed. Please check your API key and try logging in again.');
      } else if (response.status === 403) {
        // Check if it's a credit issue
        if (errorMessage.toLowerCase().includes('credit') || errorMessage.toLowerCase().includes('insufficient')) {
          throw new Error(`Insufficient credits. ${errorMessage}`);
        } else if (errorMessage.toLowerCase().includes('account')) {
          throw new Error('Access denied. Invalid account. Please check your account status.');
        }
        throw new Error('Access denied. ' + errorMessage);
      } else if (response.status === 404) {
        throw new Error('Resource not found. ' + errorMessage);
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later. ' + errorMessage);
      }
      
      throw new Error(errorMessage);
    }
    
    // Handle 204 No Content responses
    if (response.status === 204) {
      return null;
    }
    
    const responseData = await response.json();
    
    // Debug log the raw response
    console.log('MOMM API Response:', {
      url,
      status: response.status,
      responseData: JSON.stringify(responseData)
    });
    
    // Handle API response wrapper format {status, data, http_code}
    if (responseData && typeof responseData === 'object' && 'status' in responseData && 'data' in responseData) {
      // Check if the status indicates an error
      if (responseData.status === 'error' || responseData.http_code >= 400) {
        const errorMessage = responseData.data?.error || responseData.data?.message || 
                           responseData.message || 'API request failed';
        throw new Error(errorMessage);
      }
      // Return the unwrapped data
      return responseData.data;
    }
    
    // If response is not wrapped, return as is (for backwards compatibility)
    return responseData;
    } catch (fetchError) {
      // Re-throw abort errors with clear message
      if (fetchError.name === 'AbortError') {
        throw new Error(`Request was cancelled or timed out: ${requestId}`);
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('API call failed:', error);
    
    // Add more context to network errors
    if (error.message === 'Failed to fetch') {
      throw new Error('Network error. Please check your internet connection or API server availability.');
    }
    
    // Handle abort/timeout errors
    if (error.message && error.message.includes('cancelled or timed out')) {
      throw error;
    }
    
    throw error;
  } finally {
    // Clean up the request from active requests
    activeRequests.delete(requestId);
  }
};

// Helper function to cancel a specific request
export const cancelApiRequest = (requestId) => {
  if (activeRequests.has(requestId)) {
    const controller = activeRequests.get(requestId);
    controller.abort();
    activeRequests.delete(requestId);
    console.log(`🚫 Manually cancelled request: ${requestId}`);
    return true;
  }
  return false;
};

// Helper function to cancel all active requests
export const cancelAllApiRequests = () => {
  const count = activeRequests.size;
  activeRequests.forEach((controller, requestId) => {
    controller.abort();
    console.log(`🚫 Cancelled request: ${requestId}`);
  });
  activeRequests.clear();
  console.log(`🚫 Cancelled ${count} active requests`);
  return count;
}
// Chat Management Operations
export const mommChatsApi = {
  // Save chat messages (similar to SQLite save)
  save: async (messages) => {
    const config = await getApiConfig();
    
    // Group messages by chat_id
    const messagesByChat = {};
    messages.forEach(msg => {
      const chatId = msg.chat_id;
      if (!chatId) return;
      
      if (!messagesByChat[chatId]) {
        messagesByChat[chatId] = [];
      }
      messagesByChat[chatId].push(msg);
    });
    
    // Save messages for each chat
    let savedCount = 0;
    for (const [chatId, chatMessages] of Object.entries(messagesByChat)) {
      for (const message of chatMessages) {
        try {
          // Skip temporary UI messages
          if (message.isThinking || message.isLoading || 
              message.text === "Thinking..." || 
              message.text === "Loading chat history..." ||
              message.text === "Loading...") {
            continue;
          }
          
          const messageData = {
            text: message.text || null,
            media: null,
            role: message.role || 'user' // Include the role field, default to 'user' if not specified
          };
          
          // Handle media attachments - keeping media as null when no media
          // The API expects media to be null or an object with type and hash
          if (message.imageUrls && message.imageUrls.length > 0) {
            // For images, we need hashes not URLs
            // This would require uploading images first to get hashes
            console.warn('Image URLs provided but API expects image hashes. Skipping media attachment.');
          } else if (message.audioUrl || message.audioUrls) {
            console.warn('Audio URLs provided but API expects audio hashes. Skipping media attachment.');
          } else if (message.videoUrl) {
            console.warn('Video URL provided but API expects video hash. Skipping media attachment.');
          }
          
          await apiCall(`/chats/${config.accountNo}/${chatId}/messages`, {
            method: 'POST',
            body: JSON.stringify(messageData)
          });
          
          savedCount++;
        } catch (error) {
          console.error('Failed to save message:', error);
          // Continue with other messages
        }
      }
    }
    
    return savedCount;
  },
  
  // Get latest messages (all chats)
  getLatest: async () => {
    const config = await getApiConfig();
    const response = await apiCall(`/chats/${config.accountNo}?include_messages=true`);
    
    // Convert API response to match SQLite format
    const allMessages = [];
    
    if (response.chats) {
      response.chats.forEach(chat => {
        if (chat.messages) {
          chat.messages.forEach(msg => {
            const message = {
              id: msg.message_id ? parseInt(msg.message_id.split('_')[1] || Date.now()) : Date.now(),
              sender: (msg.role === 'bot' || msg.role === 'assistant') ? 'bot' : 'user', // Map role to sender for UI
              role: msg.role,
              text: msg.text,
              timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
              chat_id: chat.chatId
            };
            
            // Handle media
            if (msg.has_media && msg.media_urls) {
              message.imageUrls = msg.media_urls;
              if (msg.media_names) {
                message.imageNames = msg.media_names;
              }
            }
            
            allMessages.push(message);
          });
        }
      });
    }
    
    return { messages: allMessages };
  },
  
  // Clear all chat history
  clear: async () => {
    const config = await getApiConfig();
    console.log('🌐 mommApi.chats.clear() called with account:', config.accountNo);
    
    try {
      // Try to use the bulk delete endpoint first (more efficient)
      // This endpoint requires a confirmation parameter
      const mommConfig = await getMommApiConfig();
      const response = await fetch(`${mommConfig.apiUrl}/chats/${config.accountNo}/all?confirm=DELETE_ALL_DATA`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${config.sessionKey}`,  // Use Authorization header to avoid CORS issues
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        // If it's a 404, we'll fall back to individual deletion
        if (response.status === 404) {
          throw new Error('404');
        }
        
        // For other errors, try to get the error message
        let errorMessage = `API request failed: ${response.status}`;
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } catch (jsonError) {
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Parse the successful response
      let responseData = null;
      if (response.status !== 204) {
        responseData = await response.json();
      }
      
      console.log('All chats cleared using bulk delete endpoint');
      if (responseData && responseData.deleted_counts) {
        console.log('Deletion summary:', responseData.deleted_counts);
      }
      
      return responseData;
    } catch (error) {
      // If bulk delete endpoint is not available (404), fall back to individual deletion
      if (error.message && error.message.includes('404')) {
        console.log('Bulk delete endpoint not available, falling back to individual deletion');
        
        // Get all chats
        const response = await apiCall(`/chats/${config.accountNo}`);
        
        // Delete all chats one by one
        if (response.chats) {
          for (const chat of response.chats) {
            await apiCall(`/chats/${config.accountNo}/${chat.chatId}`, {
              method: 'DELETE'
            });
          }
        }
      } else {
        // Re-throw other errors
        throw error;
      }
    }
  },
  
  // Create new chat with optional title
  createNewChat: async (title = null) => {
    const config = await getApiConfig();
    
    // Use provided title or generate one based on current date/time
    const chatTitle = title || `Chat ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    
    const response = await apiCall(`/chats/${config.accountNo}`, {
      method: 'POST',
      body: JSON.stringify({
        title: chatTitle,
        metadata: {
          created_from: 'promptus_pwa'
        }
      })
    });
    
    // Return the chatId which is what's needed for subsequent API calls
    return response.chatId;
  },
  
  // Update chat title (for auto-generating title from first message)
  updateChatTitle: async (chatId, title) => {
    const config = await getApiConfig();
    try {
      const response = await apiCall(`/chats/${config.accountNo}/${chatId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: title
        })
      });
      console.log(`✅ Updated chat ${chatId} title to: ${title}`);
      return response;
    } catch (error) {
      console.error('Error updating chat title:', error);
      // Don't throw - title update is not critical
      return null;
    }
  },
  
  // Get all chats with pagination support
  getAllChats: async (limit = 50, requestOptions = {}) => {
    const config = await getApiConfig();
    // Add timestamp to prevent caching
    const timestamp = Date.now();
    // Use a consistent request ID for chat list loading
    const requestId = 'load-chat-list';
    // Request latest message with each chat for preview, limit to recent chats for faster loading
    const response = await apiCall(
      `/chats/${config.accountNo}?limit=${limit}&include_latest_message=true&t=${timestamp}`,
      {
        ...requestOptions,
        requestId
      }
    );
    
    // Convert to SQLite format
    const chats = [];
    
    if (response.chats) {
      for (const apiChat of response.chats) {
        // Handle various timestamp formats and fields
        let chatTimestamp;
        const timestampField = apiChat.createdAt || apiChat.created_at || apiChat.timestamp || apiChat.created;
        
        if (timestampField) {
          const parsedTime = new Date(timestampField).getTime();
          // Check if the parsed time is valid - use null if invalid to distinguish from actual time
          chatTimestamp = isNaN(parsedTime) ? null : parsedTime;
        } else {
          // Use null for missing timestamps instead of current time
          chatTimestamp = null;
          console.warn(`Chat ${apiChat.chatId || apiChat.id} has no valid timestamp`);
        }
        
        // Generate a meaningful title if missing
        let chatTitle = apiChat.title;
        if (!chatTitle || chatTitle.trim() === '') {
          // Try to generate title from latest message
          if (apiChat.latestMessage && apiChat.latestMessage.text) {
            // Use first 30 chars of the latest message as title
            const messageText = apiChat.latestMessage.text || apiChat.latestMessage.content || '';
            chatTitle = messageText.substring(0, 30) + (messageText.length > 30 ? '...' : '');
          } else {
            // Fall back to date-based title
            chatTitle = `Chat from ${new Date(chatTimestamp).toLocaleDateString()}`;
          }
        }
        
        const chat = {
          id: apiChat.chatId || apiChat.id, // Use chatId as the primary identifier, fall back to id
          numeric_id: parseInt(apiChat.id || 0), // Keep numeric ID for compatibility if needed
          timestamp: chatTimestamp,
          title: chatTitle,
          messageCount: apiChat.messageCount || apiChat.message_count || 0, // Include message count if available
          messages: [] // Empty array - messages will be loaded when chat is clicked
        };
        
        // Include latest message if available for preview
        if (apiChat.latestMessage) {
          const messageTimestampField = apiChat.latestMessage.createdAt || 
                                      apiChat.latestMessage.created_at || 
                                      apiChat.latestMessage.timestamp;
          
          let messageTimestamp = chatTimestamp; // Default to chat timestamp
          if (messageTimestampField) {
            const parsedMsgTime = new Date(messageTimestampField).getTime();
            if (!isNaN(parsedMsgTime)) {
              messageTimestamp = parsedMsgTime;
            }
          }
          
          chat.latestMessage = {
            id: apiChat.latestMessage.id,
            text: apiChat.latestMessage.text || apiChat.latestMessage.content || '',
            sender: apiChat.latestMessage.sender || apiChat.latestMessage.role || 'unknown',
            timestamp: messageTimestamp
          };
        }
        
        // NOTE: Full messages are fetched only when a chat is clicked via getChatById()
        // This significantly improves performance by reducing API calls during initial load
        
        chats.push(chat);
      }
    }
    
    console.log(`✅ Fetched ${chats.length} chats with latest message previews`);
    return chats;
  },
  
  // Get specific chat by ID with limited messages for faster loading
  getChatById: async (chatId, messageLimit = 50, requestOptions = {}) => {
    const config = await getApiConfig();
    
    // Handle both formats: if it's already a chat ID (chat_xxx), use it directly
    // Otherwise, assume it's a numeric ID and convert it
    const chatIdStr = chatId.toString().startsWith('chat_') ? chatId : `chat_${chatId}`;
    
    console.log(`📡 Fetching chat ${chatIdStr} with last ${messageLimit} messages...`);
    
    // Use a consistent request ID for chat loading to enable cancellation
    const requestId = `load-chat-${chatIdStr}`;
    
    const response = await apiCall(
      `/chats/${config.accountNo}/${chatIdStr}?include_messages=true&message_limit=${messageLimit}&t=${Date.now()}`,
      {
        ...requestOptions,
        requestId
      }
    );
    
    if (!response) return null;
    
    const chat = {
      id: response.chatId, // Use chatId as primary identifier
      numeric_id: parseInt(response.id),
      timestamp: new Date(response.createdAt).getTime(),
      title: response.title,
      messages: []
    };
    
    if (response.messages) {
      // Log message stats from API
      console.log(`📡 API returned ${response.messages.length} messages for chat ${chatIdStr}:`, {
        userMessages: response.messages.filter(m => m.role === 'user').length,
        botMessages: response.messages.filter(m => m.role === 'assistant' || m.role === 'bot').length,
        messagesWithMedia: response.messages.filter(m => m.media_urls && m.media_urls.length > 0).length
      });
      
      chat.messages = response.messages.map(msg => {
        // Separate videos from images based on file extension
        const mediaUrls = msg.media_urls || [];
        const mediaNames = msg.media_names || [];
        
        let videoUrl = null;
        const imageUrls = [];
        const imageNames = [];
        
        // Check each media URL to determine if it's a video or image
        mediaUrls.forEach((url, index) => {
          if (url && url.toLowerCase().endsWith('.mp4')) {
            // It's a video - only take the first video
            if (!videoUrl) {
              videoUrl = url;
            }
          } else if (url) {
            // It's an image
            imageUrls.push(url);
            if (mediaNames[index]) {
              imageNames.push(mediaNames[index]);
            }
          }
        });
        
        return {
          id: msg.message_id ? parseInt(msg.message_id.split('_')[1] || Date.now()) : Date.now(),
          sender: (msg.role === 'bot' || msg.role === 'assistant') ? 'bot' : 'user', // Map role to sender for UI
          role: msg.role,
          text: msg.text,
          timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
          chat_id: response.chatId,
          imageUrls: imageUrls,
          imageNames: imageNames,
          videoUrl: videoUrl,  // Add video URL if present
          // Map metadata fields if they exist
          generationId: msg.metadata?.generationId || null,
          prompt: msg.metadata?.prompt || null,
          model_used: msg.metadata?.model_used || null
        };
      });
    }
    
    return chat;
  },
  
  // Get messages for a specific chat
  getChatMessages: async (chatId) => {
    const config = await getApiConfig();
    // Handle both formats
    const chatIdStr = chatId.toString().startsWith('chat_') ? chatId : `chat_${chatId}`;
    
    const response = await apiCall(
      `/chats/${config.accountNo}/${chatIdStr}/messages?include_media=true&t=${Date.now()}`
    );
    
    if (!response || !response.messages) return [];
    
    return response.messages.map(msg => {
      // Separate videos from images based on file extension
      const mediaUrls = msg.media_urls || [];
      const mediaNames = msg.media_names || [];
      
      let videoUrl = null;
      const imageUrls = [];
      const imageNames = [];
      
      // Check each media URL to determine if it's a video or image
      mediaUrls.forEach((url, index) => {
        if (url && url.toLowerCase().endsWith('.mp4')) {
          // It's a video - only take the first video
          if (!videoUrl) {
            videoUrl = url;
          }
        } else if (url) {
          // It's an image
          imageUrls.push(url);
          if (mediaNames[index]) {
            imageNames.push(mediaNames[index]);
          }
        }
      });
      
      return {
        id: msg.message_id ? parseInt(msg.message_id.split('_')[1] || Date.now()) : Date.now(),
        sender: (msg.role === 'bot' || msg.role === 'assistant') ? 'bot' : 'user', // Map role to sender for UI
        role: msg.role,
        text: msg.text,
        timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
        chat_id: chatIdStr,
        imageUrls: imageUrls,
        imageNames: imageNames,
        videoUrl: videoUrl,  // Add video URL if present
        // Map metadata fields if they exist
        generationId: msg.metadata?.generationId || null,
        prompt: msg.metadata?.prompt || null,
        model_used: msg.metadata?.model_used || null
      };
    });
  },
  
  // Update chat title
  updateChatTitle: async (chatId, title) => {
    const config = await getApiConfig();
    // Handle both formats
    const chatIdStr = chatId.toString().startsWith('chat_') ? chatId : `chat_${chatId}`;
    
    await apiCall(`/chats/${config.accountNo}/${chatIdStr}`, {
      method: 'PUT',
      body: JSON.stringify({ title })
    });
    
    return 1; // Return changes count for compatibility
  },
  
  // Delete chat
  deleteChat: async (chatId) => {
    const config = await getApiConfig();
    // Handle both formats
    const chatIdStr = chatId.toString().startsWith('chat_') ? chatId : `chat_${chatId}`;
    
    await apiCall(`/chats/${config.accountNo}/${chatIdStr}`, {
      method: 'DELETE'
    });
    
    return 1; // Return changes count for compatibility
  },
  
  // Delete all messages for a chat
  deleteAllMessagesForChat: async (chatId) => {
    // API doesn't have a direct endpoint for this
    // Would need to delete and recreate the chat
    const config = await getApiConfig();
    // Handle both formats
    const chatIdStr = chatId.toString().startsWith('chat_') ? chatId : `chat_${chatId}`;
    
    // Get chat info first
    const chat = await apiCall(`/chats/${config.accountNo}/${chatIdStr}`);
    
    // Delete the chat
    await apiCall(`/chats/${config.accountNo}/${chatIdStr}`, {
      method: 'DELETE'
    });
    
    // Recreate with same title
    await apiCall(`/chats/${config.accountNo}`, {
      method: 'POST',
      body: JSON.stringify({
        title: chat.title || 'New Chat',
        metadata: chat.metadata || {}
      })
    });
    
    return 0; // No messages in new chat
  },
  
  // Save single message
  saveMessage: async (message) => {
    const config = await getApiConfig();
    
    if (!message.chat_id) {
      throw new Error('No chat_id provided for message');
    }
    
    // Determine sender and role with consistency validation
    let sender = message.sender;
    let role = message.role;
    
    // If sender is provided but not role, derive role from sender
    if (sender && !role) {
      if (sender === 'user') role = 'user';
      else if (sender === 'bot') role = 'assistant';
      else if (sender === 'system') role = 'system';
    }
    
    // If role is provided but not sender, derive sender from role
    if (role && !sender) {
      if (role === 'user') sender = 'user';
      else if (role === 'assistant' || role === 'bot') sender = 'bot';
      else if (role === 'system') sender = 'system';
    }
    
    // Default to user if neither provided
    if (!sender && !role) {
      sender = 'user';
      role = 'user';
    }
    
    // Validate consistency
    const validCombinations = [
      { sender: 'user', role: 'user' },
      { sender: 'bot', role: 'bot' },
      { sender: 'bot', role: 'assistant' },
      { sender: 'system', role: 'system' }
    ];
    
    const isValid = validCombinations.some(combo => 
      combo.sender === sender && combo.role === role
    );
    
    if (!isValid) {
      console.warn(`Invalid sender/role combination: sender='${sender}', role='${role}'. Adjusting...`);
      // Auto-fix common mismatches
      if (sender === 'bot' && role === 'user') role = 'assistant';
      else if (sender === 'user' && (role === 'assistant' || role === 'bot')) role = 'user';
    }
    
    const messageData = {
      text: message.text || null,
      media: null,
      sender: sender,  // Include sender field
      role: role       // Include role field
    };
    
    // Handle media - API expects media to be null or {type: 'image/audio/video', hash: 'hash_value'}
    // Since we have URLs but need hashes, we would need to upload media first
    if (message.imageUrls && message.imageUrls.length > 0) {
      console.warn('Image URLs provided but API expects image hashes. Skipping media attachment.');
    } else if (message.audioUrl || message.audioUrls) {
      console.warn('Audio URLs provided but API expects audio hashes. Skipping media attachment.');
    } else if (message.videoUrl) {
      console.warn('Video URL provided but API expects video hash. Skipping media attachment.');
    }
    
    // If we have a media hash, use it
    if (message.mediaHash && message.mediaType) {
      messageData.media = {
        type: message.mediaType,
        hash: message.mediaHash
      };
    }
    
    const response = await apiCall(`/chats/${config.accountNo}/${message.chat_id}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
    
    // API returns both user_message and bot_response
    return response;
  },
  
  // Save message with media (images, audio, video)
  saveMessageWithMedia: async (chatId, text, media = {}, metadata = {}) => {
    const config = await getApiConfig();
    
    if (!chatId) {
      throw new Error('No chat_id provided for message with media');
    }
    
    // Prepare the message payload
    const messageData = {
      text: text || null,
      sender: metadata.sender || 'bot',     // Required field for API validation
      role: metadata.role || 'assistant',  // Bot messages with media are always assistant role
      metadata: metadata  // Include metadata (e.g., generationId for videos)
    };
    
    // Build media object based on what's provided
    const mediaPayload = {};
    
    // Handle images - support both base64 and URLs
    if (media.images && media.images.length > 0) {
      mediaPayload.images = media.images.map(img => {
        // Check if this is a URL or base64 data
        if (img.url) {
          // URL format
          return {
            image_url: img.url,
            image_name: img.name || img.image_name || 'generated-image.png'
          };
        } else {
          // Base64 format
          return {
            image_data: img.data || img.image_data,
            image_name: img.name || img.image_name || 'generated-image.png'
          };
        }
      });
    }
    
    // Handle videos - send as images with image_url (API requirement)
    if (media.videos && media.videos.length > 0) {
      // Videos need to be sent in the images array with image_url field
      const videoItems = media.videos.map(vid => ({
        image_url: vid.data || vid.video_data || vid.url,  // Use image_url field for video URLs
        image_name: vid.name || vid.video_name || 'generated-video.mp4'
      }));
      
      // Add videos to images array
      if (!mediaPayload.images) {
        mediaPayload.images = [];
      }
      mediaPayload.images.push(...videoItems);
    }
    
    // Handle audio - support both base64 and URLs
    if (media.audio && media.audio.length > 0) {
      // Special handling: Audio URLs need to be sent in the images array for the API
      const audioUrlItems = [];
      const audioDataItems = [];
      
      media.audio.forEach(aud => {
        if (aud.url) {
          // Audio URLs go to images array (API requirement)
          audioUrlItems.push({
            image_url: aud.url,  // Use image_url field for audio URLs
            image_name: aud.name || aud.audio_name || 'generated-song.mp3'  // Keep .mp3 extension
          });
        } else {
          // Base64 audio data stays in audio array
          audioDataItems.push({
            audio_data: aud.data || aud.audio_data,
            audio_name: aud.name || aud.audio_name || 'generated-audio.mp3'
          });
        }
      });
      
      // Add audio URLs to images array
      if (audioUrlItems.length > 0) {
        if (!mediaPayload.images) {
          mediaPayload.images = [];
        }
        mediaPayload.images.push(...audioUrlItems);
        console.log('🎵 Audio URLs moved to images array for API:', {
          audioUrlCount: audioUrlItems.length,
          firstAudioUrl: audioUrlItems[0]?.image_url,
          firstAudioName: audioUrlItems[0]?.image_name
        });
      }
      
      // Only add audio array if we have base64 data
      if (audioDataItems.length > 0) {
        mediaPayload.audio = audioDataItems;
        console.log('🎵 Audio data being sent to API:', {
          audioCount: audioDataItems.length,
          firstAudioDataLength: audioDataItems[0]?.audio_data?.length || 0,
          firstAudioName: audioDataItems[0]?.audio_name
        });
      }
    }
    
    // Only add media to payload if we have some
    if (Object.keys(mediaPayload).length > 0) {
      messageData.media = mediaPayload;
    }
    
    console.log('📤 Sending message with media to API:', {
      chatId,
      hasText: !!text,
      hasImages: !!(mediaPayload.images && mediaPayload.images.length > 0),
      hasVideos: !!(mediaPayload.videos && mediaPayload.videos.length > 0),
      hasAudio: !!(mediaPayload.audio && mediaPayload.audio.length > 0),
      mediaStructure: messageData.media
    });
    
    // Log the complete request body for debugging
    console.log('📤 Complete request body:', JSON.stringify(messageData, (key, value) => {
      // Truncate long base64 strings for readability
      if (key === 'audio_data' && typeof value === 'string' && value.length > 100) {
        return value.substring(0, 100) + '... (truncated)';
      }
      return value;
    }, 2));
    
    const response = await apiCall(`/chats/${config.accountNo}/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
    
    console.log('📥 Message with media saved, response:', {
      message_id: response.message?.message_id || response.message_id,
      has_media: response.message?.has_media || response.has_media,
      media_urls: response.message?.media_urls || response.media_urls
    });
    
    // Return the response with media_urls at the top level for consistency
    return {
      ...response,
      media_urls: response.message?.media_urls || response.media_urls || []
    };
  },
  
  // Generate AI Response - PRIMARY endpoint for conversational AI
  // This saves the user message AND generates an AI response in one call
  generateAIResponse: async (chatId, text, options = {}) => {
    const config = await getApiConfig();
    
    if (!chatId) {
      throw new Error('No chat_id provided for AI response generation');
    }
    
    // Validate authentication credentials for MOMM API
    if (!config.sessionKey || !config.accountNo) {
      throw new Error('Authentication required. Please ensure you are logged in with a valid session.');
    }
    
    const requestData = {
      text: text,
      model: options.model || 'gpt-4o',
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 500,
      context_limit: options.context_limit || 200, // Increased default from 10 to 200 for full context
      include_system_messages: options.include_system_messages || false,
      enable_tools: true,  // Enable tools for image generation and other features
      metadata: options.metadata || {},
      // Add authentication parameters for MOMM API
      promptus_token: config.sessionKey,
      promptus_account: config.accountNo
    };
    
    // Handle media context if provided
    if (options.include_media_context) {
      requestData.include_media_context = true;
    }
    
    // Handle media attachments if provided
    if (options.media_urls) {
      requestData.has_media = true;
      requestData.media_urls = options.media_urls;
      requestData.media_names = options.media_names || [];
    }
    
    // Log the request data for debugging (remove sensitive token in production)
    console.log('MOMM API generateAIResponse request:', {
      chatId,
      accountNo: config.accountNo,
      hasToken: !!config.sessionKey,
      model: requestData.model,
      enable_tools: requestData.enable_tools,
      text: requestData.text.substring(0, 50) + '...'
    });
    
    const response = await apiCall(`/chats/${config.accountNo}/${chatId}/generate-response`, {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
    
    // Response should contain both user_message and ai_response
    if (!response.user_message || !response.ai_response) {
      throw new Error('Invalid response format from generate-response endpoint');
    }
    
    // Convert to format expected by the UI
    const userMessage = {
      id: response.user_message.message_id ? parseInt(response.user_message.message_id.split('_')[1] || Date.now()) : Date.now(),
      sender: 'user',
      role: response.user_message.role || 'user',
      text: response.user_message.text,
      timestamp: response.user_message.created_at ? new Date(response.user_message.created_at).getTime() : Date.now(),
      chat_id: chatId,
      imageUrls: response.user_message.media_urls || [],
      imageNames: response.user_message.media_names || []
    };
    
    const aiMessage = {
      id: response.ai_response.message_id ? parseInt(response.ai_response.message_id.split('_')[1] || Date.now()) : Date.now() + 1,
      sender: 'bot',
      role: response.ai_response.role || 'assistant',
      text: response.ai_response.text,
      timestamp: response.ai_response.created_at ? new Date(response.ai_response.created_at).getTime() : Date.now() + 1,
      chat_id: chatId,
      metadata: response.ai_response.metadata || {},
      // Include image URLs if present
      imageUrls: response.ai_response.media_urls || [],
      imageNames: response.ai_response.media_names || []
    };
    
    return {
      userMessage,
      aiMessage,
      model: response.model,
      tokens_used: response.tokens_used,
      context_messages: response.context_messages,
      raw_response: response // Keep the raw response for debugging
    };
  },
  
  // Search messages across chats
  searchMessages: async (query, options = {}) => {
    const config = await getApiConfig();
    
    const params = new URLSearchParams({
      q: query,
      ...(options.limit && { limit: options.limit }),
      ...(options.offset && { offset: options.offset }),
      ...(options.chat_id && { chat_id: options.chat_id })
    });
    
    const response = await apiCall(`/chats/${config.accountNo}/search?${params.toString()}`);
    
    if (!response.messages) return [];
    
    return response.messages.map(msg => ({
      id: msg.message_id ? parseInt(msg.message_id.split('_')[1] || Date.now()) : Date.now(),
      sender: (msg.role === 'bot' || msg.role === 'assistant') ? 'bot' : 'user',
      role: msg.role,
      text: msg.text,
      timestamp: msg.created_at ? new Date(msg.created_at).getTime() : Date.now(),
      chat_id: msg.chat_id,
      imageUrls: msg.media_urls || [],
      imageNames: msg.media_names || []
    }));
  },
  
  // Get chat statistics
  getChatStatistics: async () => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/chats/${config.accountNo}/stats`);
    
    return {
      total_chats: response.total_chats || 0,
      total_messages: response.total_messages || 0,
      active_chats: response.active_chats || 0,
      messages_today: response.messages_today || 0,
      messages_this_week: response.messages_this_week || 0,
      messages_this_month: response.messages_this_month || 0
    };
  }
};

// Image Management Operations
export const mommImagesApi = {
  // Save image
  save: async (hash, imageData, promptText, chatId = null, messageId = null, imageName = null) => {
    const config = await getApiConfig();
    
    const imagePayload = {
      image_data: imageData,
      image_name: imageName || 'image.png',
      prompt: promptText || '',
      chat_id: chatId,
      tags: [],
      metadata: {
        message_id: messageId
      }
    };
    
    const response = await apiCall(`/images`, {
      method: 'POST',
      body: JSON.stringify(imagePayload)
    });
    
    // Return the full response so we can get the hash
    return response;
  },
  
  // Get image by hash
  getByHash: async (hash) => {
    const config = await getApiConfig();
    
    try {
      const response = await apiCall(`/images/${hash}`);
      
      return {
        id: response.id,
        hash: response.hash,
        imageData: response.image_data,
        prompt: response.prompt,
        timestamp: new Date(response.created_at).getTime()
      };
    } catch (error) {
      // Image not found
      return null;
    }
  },
  
  // Get recent images
  getRecent: async (limit = 5) => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/images?limit=${limit}`);
    
    if (!response.images) return [];
    
    return response.images.map(img => ({
      id: img.id,
      hash: img.hash,
      imageData: img.image_data,
      prompt: img.prompt,
      timestamp: new Date(img.created_at).getTime()
    }));
  },
  
  // Get images by chat ID
  getByChatId: async (chatId) => {
    const config = await getApiConfig();
    
    // API doesn't have direct chat filtering, need to get all and filter
    const response = await apiCall(`/images?limit=1000`);
    
    if (!response.images) return [];
    
    // Filter by chat_id in metadata
    return response.images
      .filter(img => img.metadata && img.metadata.chat_id === chatId)
      .map(img => ({
        id: img.id,
        hash: img.hash,
        imageData: img.image_data,
        prompt: img.prompt,
        timestamp: new Date(img.created_at).getTime(),
        chat_id: chatId
      }));
  }
};

// Audio Management Operations
export const mommAudioApi = {
  // Save audio
  save: async (hash, audioData, transcript = null, chatId = null, messageId = null) => {
    const config = await getApiConfig();
    
    const audioPayload = {
      audio_data: audioData,
      transcript: transcript,
      chat_id: chatId,
      metadata: {
        message_id: messageId
      }
    };
    
    const response = await apiCall(`/audio`, {
      method: 'POST',
      body: JSON.stringify(audioPayload)
    });
    
    return response.id;
  },
  
  // Get audio by hash
  getByHash: async (hash) => {
    const config = await getApiConfig();
    
    try {
      const response = await apiCall(`/audio/${hash}`);
      
      return {
        id: response.id,
        hash: response.hash,
        audioData: response.audio_data,
        transcript: response.transcript,
        timestamp: new Date(response.created_at).getTime()
      };
    } catch (error) {
      return null;
    }
  },
  
  // Get audio by chat ID
  getByChatId: async (chatId) => {
    // API doesn't have direct endpoint for this
    // Would need to implement server-side filtering
    return [];
  }
};

// Video Management Operations
export const mommVideosApi = {
  // Save video
  save: async (hash, videoData, prompt = null, chatId = null, messageId = null, generationId = null) => {
    const config = await getApiConfig();
    
    const videoPayload = {
      video_data: videoData,
      prompt: prompt,
      chat_id: chatId,
      metadata: {
        message_id: messageId,
        generation_id: generationId
      }
    };
    
    const response = await apiCall(`/videos`, {
      method: 'POST',
      body: JSON.stringify(videoPayload)
    });
    
    return response.id;
  },
  
  // Get video by hash
  getByHash: async (hash) => {
    const config = await getApiConfig();
    
    try {
      const response = await apiCall(`/videos/${hash}`);
      
      return {
        id: response.id,
        hash: response.hash,
        videoData: response.video_data,
        prompt: response.prompt,
        timestamp: new Date(response.created_at).getTime()
      };
    } catch (error) {
      return null;
    }
  }
};

// Collection Management Operations
export const mommCollectionsApi = {
  // Create collection
  create: async (name, description = '') => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/collections`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        description,
        metadata: {}
      })
    });
    
    return response.id;
  },
  
  // Get all collections
  getAll: async () => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/collections`);
    
    if (!response.collections) return [];
    
    return response.collections.map(col => ({
      id: col.id,
      name: col.name,
      description: col.description,
      created_at: new Date(col.created_at).getTime(),
      updated_at: new Date(col.updated_at).getTime()
    }));
  },
  
  // Get collection by ID
  getById: async (collectionId) => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/collections/${collectionId}`);
    
    return {
      id: response.id,
      name: response.name,
      description: response.description,
      created_at: new Date(response.created_at).getTime(),
      updated_at: new Date(response.updated_at).getTime()
    };
  },
  
  // Update collection
  update: async (collectionId, name, description) => {
    const config = await getApiConfig();
    
    await apiCall(`/collections/${collectionId}`, {
      method: 'PUT',
      body: JSON.stringify({
        name,
        description
      })
    });
    
    return true;
  },
  
  // Delete collection
  delete: async (collectionId) => {
    const config = await getApiConfig();
    
    await apiCall(`/collections/${collectionId}`, {
      method: 'DELETE'
    });
    
    return true;
  },
  
  // Add image to collection
  addImage: async (imageId, collectionId, position = 0) => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/collections/${collectionId}/images`, {
      method: 'POST',
      body: JSON.stringify({
        image_id: imageId,
        position
      })
    });
    
    return response.id;
  },
  
  // Remove image from collection
  removeImage: async (imageId, collectionId) => {
    const config = await getApiConfig();
    
    await apiCall(`/collections/${collectionId}/images/${imageId}`, {
      method: 'DELETE'
    });
    
    return true;
  },
  
  // Get collection images
  getImages: async (collectionId) => {
    const config = await getApiConfig();
    
    const response = await apiCall(
      `/collections/${collectionId}?include_images=true`
    );
    
    if (!response.images) return [];
    
    return response.images.map(img => ({
      id: img.id,
      hash: img.hash,
      imageData: img.image_data,
      prompt: img.prompt,
      position: img.position,
      added_at: new Date(img.added_at).getTime()
    }));
  },
  
  // Get collections by image ID
  getByImageId: async (imageId) => {
    // Would need server-side implementation
    return [];
  }
};

// Enhanced image operations
export const mommImagesEnhancedApi = {
  ...mommImagesApi,
  
  // Mark as collected
  markAsCollected: async (hash, collectionName = null) => {
    // Would need server-side implementation
    return true;
  },
  
  // Unmark as collected
  unmarkAsCollected: async (hash) => {
    // Would need server-side implementation
    return true;
  },
  
  // Get collected images
  getCollected: async () => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/images?is_collected=true`);
    
    if (!response.images) return [];
    
    return response.images.map(img => ({
      id: img.id,
      hash: img.hash,
      imageData: img.image_data,
      prompt: img.prompt,
      is_collected: true,
      timestamp: new Date(img.created_at).getTime()
    }));
  },
  
  // Search images
  search: async (query) => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/images?tags=${encodeURIComponent(query)}`);
    
    if (!response.images) return [];
    
    return response.images.map(img => ({
      id: img.id,
      hash: img.hash,
      imageData: img.image_data,
      prompt: img.prompt,
      tags: img.tags || [],
      timestamp: new Date(img.created_at).getTime()
    }));
  },
  
  // Add tags
  addTags: async (hash, tags) => {
    // Would need server-side implementation
    return true;
  }
};

// AI Generation Operations
export const mommGenerationApi = {
  // Generate image
  generateImage: async (prompt, provider = 'openai', model = 'dall-e-3', options = {}) => {
    const config = await getApiConfig();
    
    const payload = {
      account_no: config.accountNo,
      prompt,
      provider,
      model,
      size: options.size || '1024x1024',
      quality: options.quality || 'standard',
      api_key: options.api_key,
      chat_id: options.chat_id,
      message_id: options.message_id
    };
    
    const response = await apiCall('/generate/image', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    // The API returns direct response, not wrapped in status/data
    if (response.image_id && response.hash) {
      return {
        image_id: response.image_id,
        hash: response.hash,
        image_url: response.image_url,
        credits_used: response.credits_used
      };
    }
    
    throw new Error(response.error || 'Image generation failed');
  },
  
  // Generate image with Gemini 2.0 Flash
  generateGeminiImage: async (prompt, options = {}) => {
    const config = await getApiConfig();
    
    // Use the specific Gemini endpoint
    const apiBase = await getApiBaseUrl();
    const GEMINI_URL = `${apiBase}/api/generate`;

    let ip = '';
    try {
        const response = await fetch('https://api.ipify.org');
        ip = await response.text();
        console.log(ip)
    } catch (error) {
        console.error(error)
    }

    const payload = {
      prompt: {
        prompt: prompt,
        b64image: options.image || "", // Always base64 for generation
        cosyflow: "(GenAI) Nano Banana 2.5 Flash",
        count: options.count || 1
      },
      accountNo: config.accountNo,
      ip_address: ip,
      source: electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb",
      version: process.env.REACT_APP_VERSION
    };

    payload.source = electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb"
    
    console.log('Gemini generation request:', payload);
    
    const response = await fetch(GEMINI_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain, */*'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `Gemini API request failed: ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.error || error.message || errorMessage;
      } catch (jsonError) {
        errorMessage = `${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    console.log('Gemini generation response:', data);

    // Helper function to extract error from status array
    const getStatusError = (resData) => {
      if (resData.status && Array.isArray(resData.status)) {
          const failedStatus = resData.status.find(statusObj => statusObj.status === "Failed");
          if (failedStatus && failedStatus.error) {
              return { type: 'Failed', error: failedStatus.error };
          }

          // Check for Safety status
          const safetyStatus = data.status.find(statusObj => statusObj.status === "Safety");
          if (safetyStatus && safetyStatus.error) {
              return { type: 'Safety', error: safetyStatus.error };
          }
      }
      return null;
    };
    

    // ERROR HANDLING OF ANY SORT OF FAILED JOB FROM API RESPONSE
    // Check for errors in status array first, then fall back to outer error field
    const statusError = getStatusError(data);
    const hasError = (((statusError !== null) ||
        (data.error !== undefined && data.error !== "None")));

    let finalError = null;
    if (hasError) {
        // Prioritize status error over outer error for better error details
        Swal.fire({
          title: "<strong>Out Of Credits? Go Unlimited</strong>",
          html: `<p class="text-base mb-4">Top up now to continue using MOMM and all its features</p>
                <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>`,
          icon: "error",
          showConfirmButton: false,
          
          didOpen: () => {
              const btn = document.getElementById("topUpNowBtn");
              const nxtbtn = document.getElementById("goOfflineBtn");
              if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
          }
      });
        finalError = statusError || data.error;
        throw new Error(finalError)
      } else if (statusError && statusError.type === 'Safety') {
        Swal.fire({
          title: 'Checked For Safety',
          text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
          icon: "warning",
        })
        finalError = 'Content Violation: Your prompt has violated our safety policies'
        throw new Error(finalError)
    }  
      
    // Return the UID for progress checking
    return {
      uid: data.uid,
      status: 'started'
    };
  },
  
  // Get generation output by job UID and output ID
  getGenerateOutput: async (jobUid, outputId) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    
    try {
      // Construct the full URL directly to avoid /momm prefix
      const url = `${apiBase}/api/generate/output/${jobUid}/${outputId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(config.sessionKey && { 'Authorization': `Bearer ${config.sessionKey}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch output: ${response.status}`);
      }
      
      const data = await response.json();
      
      // The response should contain the image data with base64
      return data;
    } catch (error) {
      console.error('Error fetching generate output:', error);
      throw error;
    }
  },

  setReceived: async (jobUid, outputUid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    try {
        // Construct the full URL directly to avoid /momm prefix
        const url = `${apiBase}/api/generate/received/${jobUid}/${outputUid}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                ...(config.sessionKey && { 'Authorization': `Bearer ${config.sessionKey}` })
            }
        });

        if (!response.ok) {
          throw new Error(`Failed to set file to receive: ${response.status}`);
        }

        const data = await response.json();
        console.log('[MOMM Set Received] Received endpoint ran successfully ', data);
        
        return data;
    } catch (error) {
        console.error("Error setting received:", error);
        return error;
    }
  },
  
  // Check Gemini generation progress
  checkGeminiProgress: async (uid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const PROGRESS_URL = `${apiBase}/api/generate/progress?uid=${uid}`;
    
    const response = await fetch(PROGRESS_URL, {
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Progress check failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  },
  
  // Get Gemini generated image
  getGeminiOutput: async (jobUid, imageUid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const OUTPUT_URL = `${apiBase}/api/generate/output/${jobUid}/${imageUid}`;
    
    const response = await fetch(OUTPUT_URL, {
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get image: ${response.status}`);
    }
    
    // Try to get the response as JSON first
    try {
      const data = await response.json();
      console.log('Gemini output response (JSON):', data);
      
      // If it's already JSON with an image property
      if (data.image) {
        return `data:image/png;base64,${data.image}`;
      }
      
      // If the response has some other structure
      throw new Error('Unexpected response format');
    } catch (jsonError) {
      // If JSON parsing fails, try as text
      const responseText = await response.text();
      console.log('Gemini output response (text):', responseText.substring(0, 100) + '...');
      
      try {
        // Try to decode as base64
        const decodedString = atob(responseText);
        const decodedData = JSON.parse(decodedString);
        
        if (decodedData.image) {
          return `data:image/png;base64,${decodedData.image}`;
        }
      } catch (decodeError) {
        // If that fails too, assume the response itself is the base64 image
        console.log('Treating response as direct base64 image');
        return `data:image/png;base64,${responseText}`;
      }
    }
  },

  // Generate image with GPT-Image-1 (for 4o iterations)
  generateNanoBananaImage: async (prompt, options = {}) => {
    const config = await getApiConfig();
    
    // Debug logging
    console.log('generateNanoBananaImage called with options:', {
      cosyflow: options.cosyflow,
      size: options.size
    });
    
    let ip = '';
    try {
        const response = await fetch('https://api.ipify.org');
        ip = await response.text();
        console.log(ip)
    } catch (error) {
        console.error(error)
    }

    // Use the specific GPT-Image-1 endpoint
    const apiBase = await getApiBaseUrl();
    const GPT_URL = `${apiBase}/api/generate`;
    
    const payload = {
      prompt: {
        // openai_model: "gpt-image-1",
        prompt: prompt,
        b64image_0: options.input_image || "", // Add input_image field for image-to-image
        count: 1,
        scheduler: "DPM-Solver",
        cosyflow: options.cosyflow || "(GenAI) Nano Banana 2.5 Flash",
        safe_mode: true,
      },
      accountNo: config.accountNo,
      ip_address: ip,
      source: electronBridge.isElectron ? "PWA" : "PWeb",
      version: process.env.REACT_APP_VERSION
    };
    
    console.log('Nano Banana 2.5 Flash generation request payload:', {
      promptText: payload.prompt.prompt,
      fullPayload: payload
    });
    
    const response = await fetch(GPT_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain, */*'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `Nano Banana 2.5 Flash generation failed: ${response.status}`;
      let errorDetails = '';
      try {
        const errorText = await response.text();
        console.error('Nano Banana 2.5 Flash generation failed - Response:', errorText);
        console.error('Nano Banana 2.5 Flash generation failed - Status:', response.status);
        console.error('Nano Banana 2.5 Flash generation failed - Headers:', response.headers);
        errorDetails = errorText;
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      throw new Error(`${errorMessage} - ${errorDetails}`);
    }
    
    const data = await response.json();
    console.log('Nano Banana 2.5 Flash generation response:', data);
    
    // Helper function to extract error from status array
    const getStatusError = (resData) => {
      if (resData.status && Array.isArray(resData.status)) {
          const failedStatus = resData.status.find(statusObj => statusObj.status === "Failed");
          if (failedStatus && failedStatus.error) {
              return { type: 'Failed', error: failedStatus.error };
          }

          // Check for Safety status
          const safetyStatus = data.status.find(statusObj => statusObj.status === "Safety");
          if (safetyStatus && safetyStatus.error) {
              return { type: 'Safety', error: safetyStatus.error };
          }
      }
      return null;
    };

    // ERROR HANDLING OF ANY SORT OF FAILED JOB FROM API RESPONSE
    // Check for errors in status array first, then fall back to outer error field
    const statusError = getStatusError(data);
    const hasError = (((statusError !== null) ||
        (data.error !== undefined && data.error !== "None")));

    let finalError = null;
    if (hasError) {
        // Prioritize status error over outer error for better error details
        Swal.fire({
          title: "<strong>Out Of Credits? Go Unlimited</strong>",
          html: `<p class="text-base mb-4">Top up now to continue using MOMM and all its features</p>
                <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>`,
          icon: "error",
          showConfirmButton: false,
          
          didOpen: () => {
              const btn = document.getElementById("topUpNowBtn");
              const nxtbtn = document.getElementById("goOfflineBtn");
              if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
          }
      });
        finalError = statusError || data.error;
    } else if (statusError && statusError.type === 'Safety') {
        Swal.fire({
          title: 'Checked For Safety',
          text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
          icon: "warning",
        })
        finalError = 'Content Violation: Your prompt has violated our safety policies'
    }

    // Return the UID for progress checking
    return {
      uid: data.uid,
      status: 'started',
      error: finalError
    };
  },
  
  // Generate image with GPT-Image-1 (for 4o iterations)
  generateGPTImage: async (prompt, options = {}) => {
    const config = await getApiConfig();
    
    // Debug logging
    console.log('generateGPTImage called with options:', {
      hasInputImage: !!options.input_image,
      inputImageLength: options.input_image ? options.input_image.length : 0,
      inputImagePreview: options.input_image ? options.input_image.substring(0, 50) + '...' : 'NONE',
      cosyflow: options.cosyflow,
      size: options.size
    });
    
    // Use the specific GPT-Image-1 endpoint
    const apiBase = await getApiBaseUrl();
    const GPT_URL = `${apiBase}/api/generate`;

    let ip = '';
    try {
        const response = await fetch('https://api.ipify.org');
        ip = await response.text();
        console.log(ip)
    } catch (error) {
        console.error(error)
    }
    
    const payload = {
      prompt: {
        // openai_model: "gpt-image-1",
        prompt: prompt,
        b64image_0: options.input_image || "", // Add input_image field for image-to-image
        size: options.size || "1024x1024",
        n: 1,
        scheduler: "DPM-Solver",
        cosyflow: options.cosyflow || "(OpenAI) GPT-Image-1: Image to Image",
        // model: "(OpenAI) GPT-Image-1: Image to Image",
        count: 1,
        safe_mode: true,
        endpoint: 'edit'
      },
      accountNo: config.accountNo,
      ip_address: ip,
      source: electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb",
      version: process.env.REACT_APP_VERSION
    };

    payload.source = electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb"
    
    console.log('GPT-Image-1 generation request payload:', {
      promptText: payload.prompt.prompt,
      hasInputImage: !!payload.prompt.b64image_0,
      inputImageLength: payload.prompt.b64image_0.length,
      fullPayload: payload
    });
    
    const response = await fetch(GPT_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain, */*'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `GPT-Image-1 generation failed: ${response.status}`;
      let errorDetails = '';
      try {
        const errorText = await response.text();
        console.error('GPT-Image-1 generation failed - Response:', errorText);
        console.error('GPT-Image-1 generation failed - Status:', response.status);
        console.error('GPT-Image-1 generation failed - Headers:', response.headers);
        errorDetails = errorText;
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      throw new Error(`${errorMessage} - ${errorDetails}`);
    }
    
    const data = await response.json();
    console.log('GPT-Image-1 generation response:', data);

    // Helper function to extract error from status array
    const getStatusError = (resData) => {
      if (resData.status && Array.isArray(resData.status)) {
          const failedStatus = resData.status.find(statusObj => statusObj.status === "Failed");
          if (failedStatus && failedStatus.error) {
              return { type: 'Failed', error: failedStatus.error };
          }

          // Check for Safety status
          const safetyStatus = data.status.find(statusObj => statusObj.status === "Safety");
          if (safetyStatus && safetyStatus.error) {
              return { type: 'Safety', error: safetyStatus.error };
          }
      }
      return null;
    };

    // ERROR HANDLING OF ANY SORT OF FAILED JOB FROM API RESPONSE
    // Check for errors in status array first, then fall back to outer error field
    const statusError = getStatusError(data);
    const hasError = (((statusError !== null) ||
        (data.error !== undefined && data.error !== "None")));

    let finalError = null;
    if (hasError) {
        // Prioritize status error over outer error for better error details
        Swal.fire({
          title: "<strong>Out Of Credits? Go Unlimited</strong>",
          html: `<p class="text-base mb-4">Top up now to continue using MOMM and all its features</p>
                <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>`,
          icon: "error",
          showConfirmButton: false,
          
          didOpen: () => {
              const btn = document.getElementById("topUpNowBtn");
              const nxtbtn = document.getElementById("goOfflineBtn");
              if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
          }
      });
        finalError = statusError || data.error;
        throw new Error(finalError)
    } else if (statusError && statusError.type === 'Safety') {
        Swal.fire({
          title: 'Checked For Safety',
          text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
          icon: "warning",
        })
        finalError = 'Content Violation: Your prompt has violated our safety policies'
        throw new Error(finalError)
    }
    
    // Return the UID for progress checking
    return {
      uid: data.uid,
      status: 'started',
      error: finalError
    };
  },
  
  // Generate audio
  generateAudio: async (text, options = {}) => {
    const config = await getApiConfig();
    
    const payload = {
      account_no: config.accountNo,
      text,
      voice: options.voice || 'voice1',
      language: options.language || 'en-US',
      speed: options.speed || 1.0,
      pitch: options.pitch || 1.0,
      chat_id: options.chat_id,
      message_id: options.message_id
    };
    
    const response = await apiCall('/generate/audio', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    // The API returns direct response, not wrapped in status/data
    if (response.audio_id && response.hash) {
      return {
        audio_id: response.audio_id,
        hash: response.hash,
        audio_url: response.audio_url,
        duration: response.duration,
        credits_used: response.credits_used
      };
    }
    
    throw new Error(response.error || 'Audio generation failed');
  },
  
  // Get generation history
  getHistory: async (type = 'all', limit = 50) => {
    const config = await getApiConfig();
    
    const response = await apiCall(
      `/generate/history/${config.accountNo}?type=${type}&limit=${limit}`
    );
    
    return response.history || [];
  },
  
  // Get available models
  getModels: async () => {
    const response = await apiCall('/generate/models');
    return response;
  },
  
  // Get generation costs
  getCosts: async () => {
    const response = await apiCall('/generate/costs');
    return response;
  },
  
  // Generate song from image or text
  generateSong: async (options = {}) => {
    const config = await getApiConfig();
    
    // Use the specific song generation endpoint
    const apiBase = await getApiBaseUrl();
    const SONG_URL = `${apiBase}/api/generate`;

    let ip = '';
    try {
        const response = await fetch('https://api.ipify.org');
        ip = await response.text();
        console.log(ip)
    } catch (error) {
        console.error(error)
    }
    
    const payload = {
      prompt: {
        udio_model: options.model || "chirp-v4-5",
        title: options.title || "Generated Music",
        prompt: options.prompt || "Generate a song from this image",
        gpt_description_prompt: options.gpt_description_prompt || "",
        make_instrumental: options.make_instrumental || false,
        custom_mode: options.custom_mode || false,
        scheduler: options.scheduler || "DPM-Solver",
        cosyflow: options.cosyflow || "(Udio) Chirp Music Generation",
        model: options.modelName || "(Udio) Chirp Music Generation",
        count: options.count || 1,
        image_url: options.image_url  // Include image URL for backend to process
      },
      accountNo: config.accountNo,
      ip_address: ip,
      source: electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb",
      version: process.env.REACT_APP_VERSION
    };
    
    payload.source = electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb"

    console.log('Song generation request:', payload);
    
    const response = await fetch(SONG_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `Song generation failed: ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.error || error.message || errorMessage;
      } catch (e) {
        // Use status text if response is not JSON
        errorMessage = `${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();

    if (data.error && data.error !== '') {
      throw new Error(data.error)
    }

    return data;
  },
  
  // Check song generation status (for offline mode with workId)
  checkSongStatus: async (workId) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    
    const STATUS_URL = `${apiBase}/api/audio/status/${workId}`;
    
    const response = await fetch(STATUS_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      let errorMessage = `Song status check failed: ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.error || error.message || errorMessage;
      } catch (e) {
        errorMessage = `${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  },
  
  // Check song generation progress (for online mode with uid)
  checkSongProgress: async (uid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const PROGRESS_URL = `${apiBase}/api/generate/progress?uid=${uid}`;
    
    console.log('Checking song progress:', {
      uid,
      url: PROGRESS_URL,
      hasSessionKey: !!config.sessionKey
    });
    
    try {
      const response = await fetch(PROGRESS_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${config.sessionKey}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        mode: 'cors'
        // Removed credentials: 'include' to avoid CORS conflict with wildcard origin
      });
      
      // Handle both success and error responses
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response:', responseText);
        // If we can't parse the response, create a default progress object
        data = {
          status: 'processing',
          progress: 0,
          message: 'Unable to get progress update'
        };
      }
      
      if (!response.ok) {
        // Log the error but don't throw immediately
        console.error(`Progress check returned ${response.status}:`, data);
        
        // If it's a 500 error, the server might be having issues
        // Return a default progress instead of throwing
        if (response.status === 500) {
          return {
            status: 'processing',
            progress: 0,
            message: 'Server is processing your request...',
            error: true
          };
        }
        
        // For other errors, throw with more context
        throw new Error(data.error || data.message || `Progress check failed: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('Song progress check error:', error);
      
      // If it's a network error or CORS issue, return a default progress
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          status: 'processing',
          progress: 0,
          message: 'Checking progress...',
          error: true
        };
      }
      
      throw error;
    }
  },
  
  // Get song output (for online mode)
  getSongOutput: async (jobUid, audioUid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const OUTPUT_URL = `${apiBase}/api/generate/output/${jobUid}/${audioUid}`;
    
    const response = await fetch(OUTPUT_URL, {
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get audio: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Return the audio URL
    if (data.image) {
      // The unified API returns audio as base64 in the 'image' field
      return `data:audio/mpeg;base64,${data.image}`;
    } else if (data.audio) {
      return `data:audio/mpeg;base64,${data.audio}`;
    } else if (data.url) {
      return data.url;
    } else {
      throw new Error('No audio data in response');
    }
  },
  
  // Generate video from image using Promptus API (for online mode)
  generateVideo: async (options = {}) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const VIDEO_URL = `${apiBase}/api/generate`;
    
    // Get client IP if available
    const clientIP = options.ip_address || window.clientIP || "0.0.0.0";
    
    // Check if this is an extension or new generation
    const isExtension = options.extend && options.extend_from_video_id;
    
    const payload = {
      prompt: {
        prompt: options.prompt || "Transform this image into a captivating video",
        aspect_ratio: options.aspect_ratio || "16:9",
        resolution: options.resolution || "540p",
        duration: options.duration || "5s",
        loop: options.loop || false,
        model: options.model || "ray-flash-2",
        generation_type: "video",
        count: 1,
        safe_mode: true
      },
      accountNo: config.accountNo,
      ip_address: clientIP,
      source: electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb",
      version: process.env.REACT_APP_VERSION
    };

    payload.source = electronBridge.isElectron ? "MOMM-PWA" : "MOMM-PWeb" 
    
    // Configure keyframes based on extension or new generation
    if (isExtension) {
      // Extension mode - use generation ID
      payload.prompt["keyframes.frame0.id"] = "";  // This should be empty string
      payload.prompt["keyframes.frame0.type"] = "generation";
      payload.prompt.cosyflow = "(Luma) Dream Machine Extend Video";
      payload.prompt.keyframes = {
        frame0: {
          id: options.extend_from_video_id || ""  // The actual generation ID goes here
        }
      };
    } else {
      // New generation mode - use image URL
      payload.prompt["keyframes.frame0.url"] = options.image_url || "";
      payload.prompt["keyframes.frame0.type"] = "image";
      payload.prompt.cosyflow = "(Luma) Dream Machine Image to Video (First frame)";
      payload.prompt.keyframes = {
        frame0: {
          url: options.image_url || "",
          type: "image"
        }
      };
    }
    
    console.log('Video generation request:', {
      url: VIDEO_URL,
      payload,
      hasSessionKey: !!config.sessionKey
    });
    
    const response = await fetch(VIDEO_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      let errorMessage = `Video generation failed: ${response.status}`;
      try {
        const error = await response.json();
        errorMessage = error.error || error.message || errorMessage;
      } catch (e) {
        errorMessage = `${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    console.log('Video generation response:', data);

    // Helper function to extract error from status array
    const getStatusError = (resData) => {
      if (resData.status && Array.isArray(resData.status)) {
          const failedStatus = resData.status.find(statusObj => statusObj.status === "Failed");
          if (failedStatus && failedStatus.error) {
              return { type: 'Failed', error: failedStatus.error };
          }

          // Check for Safety status
          const safetyStatus = data.status.find(statusObj => statusObj.status === "Safety");
          if (safetyStatus && safetyStatus.error) {
              return { type: 'Safety', error: safetyStatus.error };
          }
      }
      return null;
    };

    // ERROR HANDLING OF ANY SORT OF FAILED JOB FROM API RESPONSE
    // Check for errors in status array first, then fall back to outer error field
    const statusError = getStatusError(data);
    const hasError = (((statusError !== null) ||
        (data.error !== undefined && data.error !== "None")));

    let finalError = null;
    if (hasError) {
        // Prioritize status error over outer error for better error details
        Swal.fire({
          title: "<strong>Out Of Credits? Go Unlimited</strong>",
          html: `<p class="text-base mb-4">Top up now to continue using MOMM and all its features</p>
                <button id="topUpNowBtn" class="rounded-lg bg-yellow py-3 px-5 shadow-xl font-bold text-xl text-white uppercase">Top Up Now</button>`,
          icon: "error",
          showConfirmButton: false,
          
          didOpen: () => {
              const btn = document.getElementById("topUpNowBtn");
              const nxtbtn = document.getElementById("goOfflineBtn");
              if (btn) btn.addEventListener("click", () => openPromptusCreditsStore('payments'));
          }
      });
        finalError = statusError || data.error;
        throw new Error(finalError)
    } else if (statusError && statusError.type === 'Safety') {
        Swal.fire({
          title: 'Checked For Safety',
          text: 'Some words in your prompt may trigger content filters. Edit your prompt or generate offline',
          icon: "warning",
        })
        finalError = 'Content Violation: Your prompt has violated our safety policies'
        throw new Error(finalError)
    }

    return data;
  },
  
  // Check video generation progress (for online mode with uid)
  checkVideoProgress: async (uid) => {
    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const PROGRESS_URL = `${apiBase}/api/generate/progress?uid=${uid}`;
    
    console.log('Checking video progress:', {
      uid,
      url: PROGRESS_URL
    });
    
    try {
      const response = await fetch(PROGRESS_URL, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${config.sessionKey}`,
          'Accept': 'application/json'
        }
      });
      
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse video progress response:', responseText);
        data = {
          status: 'processing',
          progress: 0,
          message: 'Processing video...'
        };
      }
      
      if (!response.ok) {
        console.error(`Video progress check returned ${response.status}:`, data);
        
        // Return a default progress for 500 errors
        if (response.status === 500) {
          return {
            status: 'processing',
            progress: 0,
            message: 'Server is processing your video...',
            error: true
          };
        }
        
        throw new Error(data.error || data.message || `Progress check failed: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('Video progress check error:', error);
      
      // Return default progress for network errors
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        return {
          status: 'processing',
          progress: 0,
          message: 'Checking video progress...',
          error: true
        };
      }
      
      throw error;
    }
  },
  
  // Get video output (for online mode)
  getVideoOutput: async (jobUid, videoUid) => {
    // Wait 3 seconds to allow API to finalize video processing
    console.log('⏳ Waiting 3 seconds before fetching video output...');
    await new Promise(resolve => setTimeout(resolve, 3000));

    const config = await getApiConfig();
    const apiBase = await getApiBaseUrl();
    const OUTPUT_URL = `${apiBase}/api/generate/output/${jobUid}/${videoUid}`;

    console.log('Getting video output:', {
      jobUid,
      videoUid,
      url: OUTPUT_URL
    });
    
    const response = await fetch(OUTPUT_URL, {
      headers: {
        'Authorization': `Bearer ${config.sessionKey}`,
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get video: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Video output response:', data);
    
    // Parse the params field to extract the generation ID
    let generationId = null;
    if (data.params) {
      try {
        const params = typeof data.params === 'string' ? JSON.parse(data.params) : data.params;
        generationId = params.id || null;
        console.log('Extracted generation ID from params:', generationId);
      } catch (parseError) {
        console.error('Error parsing params for generation ID:', parseError);
      }
    }
    
    // Construct the direct video URL
    // Response contains: { "url": "https://api.dev.promptus.ai//output", ... }
    // We need to build: https://api.dev.promptus.ai/images/output/{jobUid}/{videoUid}.mp4
    
    if (data.url) {
      // Get the base API URL (e.g., https://api.dev.promptus.ai)
      // Extract base URL without the path
      const urlMatch = data.url.match(/^(https?:\/\/[^\/]+)/);
      const baseApiUrl = urlMatch ? urlMatch[1] : apiBase;
      
      // Construct the final video URL with /images/output path
      const videoUrl = `${baseApiUrl}/output/${jobUid}/${videoUid}.mp4`;
      
      console.log('Constructed video URL:', videoUrl);
      
      // Return both the URL and the generation ID
      return {
        url: videoUrl,
        generationId: generationId
      };
    } else {
      throw new Error('No URL in video output response');
    }
  }
};

// Search Operations
export const mommSearchApi = {
  // Global search across all content
  search: async (query, filters = {}) => {
    const config = await getApiConfig();
    
    const params = new URLSearchParams({
      q: query,
      ...(filters.type && { type: filters.type }),
      ...(filters.limit && { limit: filters.limit }),
      ...(filters.offset && { offset: filters.offset })
    });
    
    const response = await apiCall(`/search?${params.toString()}`);
    
    return {
      results: response.results || [],
      total: response.total || 0,
      query: query
    };
  }
};

// Statistics Operations
export const mommStatsApi = {
  // Get usage statistics
  getStats: async (period = 'all') => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/stats/${config.accountNo}?period=${period}`);
    
    return {
      total_chats: response.total_chats || 0,
      total_messages: response.total_messages || 0,
      total_images: response.total_images || 0,
      total_audio: response.total_audio || 0,
      total_video: response.total_video || 0,
      credits_used: response.credits_used || 0,
      credits_remaining: response.credits_remaining || 0,
      period: period
    };
  },
  
  // Get credit balance
  getCreditBalance: async () => {
    const config = await getApiConfig();
    
    const response = await apiCall(`/user/credits/${config.accountNo}`);
    
    return {
      balance: response.balance || 0,
      currency: response.currency || 'credits'
    };
  }
};

// Helper function for proper media upload flow
// First upload media to get hash, then use hash in messages
export const uploadMediaAndSendMessage = async (chatId, text, mediaFile, mediaType) => {
  const config = await getApiConfig();
  let mediaHash = null;
  let mediaData = null;
  
  // Step 1: Upload media if provided
  if (mediaFile) {
    try {
      // Convert file to base64 if needed
      if (mediaFile instanceof File || mediaFile instanceof Blob) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve, reject) => {
          reader.onload = e => resolve(e.target.result.split(',')[1]);
          reader.onerror = reject;
        });
        reader.readAsDataURL(mediaFile);
        mediaData = await base64Promise;
      } else if (typeof mediaFile === 'string') {
        // Assume it's already base64
        mediaData = mediaFile.replace(/^data:.*?;base64,/, '');
      }
      
      // Upload based on media type
      let uploadResponse;
      if (mediaType === 'image') {
        uploadResponse = await mommImagesApi.save(null, mediaData, text, chatId, null, mediaFile.name || 'image.png');
        mediaHash = uploadResponse.hash || uploadResponse.image_hash;
      } else if (mediaType === 'audio') {
        uploadResponse = await mommAudioApi.save(null, mediaData, null, chatId);
        mediaHash = uploadResponse.hash || uploadResponse.audio_hash;
      } else if (mediaType === 'video') {
        uploadResponse = await mommVideosApi.save(null, mediaData, text, chatId);
        mediaHash = uploadResponse.hash || uploadResponse.video_hash;
      }
    } catch (error) {
      console.error('Failed to upload media:', error);
      // Continue without media if upload fails
    }
  }
  
  // Step 2: Send message with optional media hash
  const messageData = {
    chat_id: chatId,
    text: text,
    role: 'user'
  };
  
  if (mediaHash && mediaType) {
    messageData.mediaHash = mediaHash;
    messageData.mediaType = mediaType;
  }
  
  // Use the generate AI response endpoint if we want an AI response
  const response = await mommChatsApi.generateAIResponse(chatId, text, {
    media_hash: mediaHash,
    media_type: mediaType
  });
  
  return response;
};

// Helper to upload media and attach to existing message
export const uploadMediaForMessage = async (mediaFile, mediaType, chatId = null, messageId = null) => {
  let mediaData = null;
  
  // Convert file to base64 if needed
  if (mediaFile instanceof File || mediaFile instanceof Blob) {
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = e => resolve(e.target.result.split(',')[1]);
      reader.onerror = reject;
    });
    reader.readAsDataURL(mediaFile);
    mediaData = await base64Promise;
  } else if (typeof mediaFile === 'string') {
    // Assume it's already base64
    mediaData = mediaFile.replace(/^data:.*?;base64,/, '');
  }
  
  // Upload based on media type
  let uploadResponse;
  if (mediaType === 'image') {
    uploadResponse = await mommImagesApi.save(null, mediaData, '', chatId, messageId, mediaFile.name || 'image.png');
    return {
      hash: uploadResponse.hash || uploadResponse.image_hash,
      url: uploadResponse.image_url || `/media/images/${uploadResponse.hash || uploadResponse.image_hash}`,
      type: 'image'
    };
  } else if (mediaType === 'audio') {
    uploadResponse = await mommAudioApi.save(null, mediaData, null, chatId, messageId);
    return {
      hash: uploadResponse.hash || uploadResponse.audio_hash,
      url: uploadResponse.audio_url || `/media/audio/${uploadResponse.hash || uploadResponse.audio_hash}`,
      type: 'audio'
    };
  } else if (mediaType === 'video') {
    uploadResponse = await mommVideosApi.save(null, mediaData, '', chatId, messageId);
    return {
      hash: uploadResponse.hash || uploadResponse.video_hash,
      url: uploadResponse.video_url || `/media/videos/${uploadResponse.hash || uploadResponse.video_hash}`,
      type: 'video'
    };
  }
  
  throw new Error(`Unsupported media type: ${mediaType}`);
};

// Export all API modules
export default {
  chats: mommChatsApi,
  images: mommImagesApi,
  audio: mommAudioApi,
  videos: mommVideosApi,
  collections: mommCollectionsApi,
  imagesEnhanced: mommImagesEnhancedApi,
  generation: mommGenerationApi,
  search: mommSearchApi,
  stats: mommStatsApi,
  // Helper functions
  uploadMediaAndSendMessage,
  uploadMediaForMessage
};